import { Button, Flex, IconNotepadLine, Pill, Text } from "@instructure/ui";
import { memo, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Skeleton from "react-loading-skeleton";
import { Link } from "react-router";
import {
  ERROR,
  useNotification,
} from "../../../../components/CustomHooks/NotificationProvider";
import { ReviewService } from "../../../../services/ReviewService";
import { UserDetailContext } from "../../../../utils/Context";

const CourseReviewCycleDetails = memo(({ section, cycle }) => {
  const { t, i18n } = useTranslation();
  const userDetails = useContext(UserDetailContext);
  const showNotification = useNotification();
  const [loading, setLoading] = useState(true);
  const [startYear, setStartYear] = useState("");
  const [endYear, setEndYear] = useState("");

  const courseCoordinators = section.offering.parent_course_coordinators || [];

  useEffect(() => {
    async function fetchCycleDetails() {
      try {
        setLoading(true);
        const reviewList = await ReviewService.getReviewsByContextId(
          section.offering.parent_course_id
        );
        const currentReview = reviewList[0];
        if (currentReview) {
          const startYear =
            currentReview.academic_years[0]?.name || t("global.na");
          const endYear =
            currentReview.academic_years[
              currentReview.academic_years.length - 1
            ]?.name || t("global.na");
          setStartYear(startYear);
          setEndYear(endYear);
        } else {
          setStartYear("");
          setEndYear("");
        }
        setLoading(false);
      } catch {
        showNotification(
          t("notifications.general.fetchError", {
            obj: t("courseReviewCycle.cycleInformation"),
          }),
          ERROR
        );
      }
    }
    fetchCycleDetails();
  }, [section, showNotification]);

  function listOfCoordinators(coordinators) {
    if (coordinators.length === 0) {
      return <Text fontStyle="italic">{t("global.none")}</Text>;
    }
    const formatter = new Intl.ListFormat(i18n.language, {
      style: "long",
      type: "conjunction",
    });
    return formatter.format(coordinators);
  }

  function coordinatingCourse() {
    return (
      !userDetails.isFaculty() &&
      userDetails.permissions?.coordinatedCourses?.includes(
        section.offering.parent_course_id
      )
    );
  }

  return (
    <Flex gap="small" padding="0 0 small 0">
      {loading ? (
        <>
          <Flex.Item shouldGrow>
            <Skeleton height="1.5rem" />
          </Flex.Item>
          <Flex.Item width="25%">
            <Skeleton height="1.5rem" />
          </Flex.Item>
        </>
      ) : (
        <>
          <Flex.Item shouldGrow shouldShrink>
            <Text weight="bold">{t("user.courseCoordinators")}:</Text>{" "}
            {listOfCoordinators(courseCoordinators)}
          </Flex.Item>
          <Flex.Item shouldShrink>
            <Text weight="bold">
              {t("courseReviewCycle.currentCyclePeriod")}:
            </Text>{" "}
            {startYear && endYear ? (
              <>
                <Pill color={startYear === t("global.na") ? "warning" : "info"}>
                  {startYear}
                </Pill>
                {" - "}
                <Pill color={endYear === t("global.na") ? "warning" : "info"}>
                  {endYear}
                </Pill>
              </>
            ) : (
              <Text fontStyle="italic">
                {t("courseReviewCycle.noReviewInProgress")}
              </Text>
            )}
          </Flex.Item>
          {coordinatingCourse() && (
            <Flex.Item>
              <Link
                to="/course-review/home"
                state={{ section: section, cycle }}
              >
                <Button
                  color="primary"
                  size="small"
                  renderIcon={IconNotepadLine}
                >
                  {t("courseReview.courseReview")}
                </Button>
              </Link>
            </Flex.Item>
          )}
        </>
      )}
    </Flex>
  );
}, arePropsEqual);

function arePropsEqual(prevProps, nextProps) {
  return (
    prevProps.section.id === nextProps.section.id &&
    prevProps.cycle?.id === nextProps.cycle?.id
  );
}

export default CourseReviewCycleDetails;
